/**
 * Reading a decimal amount a person typed, in their locale.
 *
 * The API takes an amount as a plain decimal string (`"1250.50"`), never a
 * number, so precision is never handed to an IEEE double. What a person types
 * is another matter: `12,50` is twelve and a half in Turkey and Germany, and
 * stripping every comma — which is what the operations forms used to do —
 * turned it into one thousand two hundred and fifty, which the backend then
 * happily recorded.
 *
 * So the separators come from the locale, and anything that could be read two
 * ways is refused rather than guessed: the form says so on the field, and the
 * person types it the one way that is unambiguous.
 */

/** Why a typed amount was refused. */
export type DecimalInputProblem = 'empty' | 'invalid' | 'ambiguous' | 'negative' | 'precision'

export type DecimalInputResult =
  { ok: true; value: string } | { ok: false; problem: DecimalInputProblem }

/** The decimal and grouping separators a locale writes numbers with. */
export function separatorsOf(locale: string): { decimal: string; group: string } {
  const parts = new Intl.NumberFormat(locale, { useGrouping: true }).formatToParts(12345.6)
  const decimal = parts.find((part) => part.type === 'decimal')?.value ?? '.'
  const group = parts.find((part) => part.type === 'group')?.value ?? ','
  return { decimal, group }
}

// Spaces of every kind: some locales group with a (narrow) no-break space, and
// a pasted figure often carries one.
const SPACES = /[\s\u00a0\u202f]/g
const DIGITS = /^\d+$/

/**
 * A typed amount as the API's decimal string, or why it cannot be one.
 *
 * Accepted: digits, the locale's decimal separator once, and the locale's
 * grouping separator between proper groups of three (`1,250.50` in English,
 * `1.250,50` in German). Spaces are ignored.
 *
 * Refused as **ambiguous**: the other locale's decimal separator (`12,50`
 * typed where `.` is the decimal point), and a lone grouping separator with no
 * decimal part (`1,250`), which a reader from the other half of Europe takes
 * for one and a quarter.
 *
 * Args:
 *   text: What was typed.
 *   locale: The reader's locale, as `lib/format` takes it.
 *   options.allowNegative: Accept a leading minus (an opening balance).
 *   options.maxFractionDigits: The most decimals the API keeps.
 */
export function parseDecimalInput(
  text: string,
  locale: string,
  options: { allowNegative?: boolean; maxFractionDigits?: number } = {},
): DecimalInputResult {
  const { allowNegative = false, maxFractionDigits = 4 } = options
  let compact = text.replace(SPACES, '')
  if (!compact) return { ok: false, problem: 'empty' }

  let sign = ''
  if (compact.startsWith('-') || compact.startsWith('\u2212')) {
    if (!allowNegative) return { ok: false, problem: 'negative' }
    sign = '-'
    compact = compact.slice(1)
  }

  const { decimal, group } = separatorsOf(locale)
  const decimalAt = compact.indexOf(decimal)
  if (decimalAt !== compact.lastIndexOf(decimal)) return { ok: false, problem: 'invalid' }

  const whole = decimalAt === -1 ? compact : compact.slice(0, decimalAt)
  const fraction = decimalAt === -1 ? '' : compact.slice(decimalAt + 1)
  if (decimalAt !== -1 && !DIGITS.test(fraction)) {
    // A grouping mark after the decimal point is a number written the other way round.
    return { ok: false, problem: fraction.includes(group) ? 'ambiguous' : 'invalid' }
  }

  const groups = whole.split(group)
  const grouped = groups.length > 1
  const wellGrouped =
    groups.every((part) => DIGITS.test(part)) &&
    groups.slice(1).every((part) => part.length === 3) &&
    (groups[0]?.length ?? 0) <= 3
  if (grouped && !wellGrouped) {
    // `12,50` where `,` groups: either a decimal written the other locale's
    // way or a typo — not something to guess at with money.
    return {
      ok: false,
      problem: groups.every((part) => DIGITS.test(part)) ? 'ambiguous' : 'invalid',
    }
  }
  if (!grouped && !DIGITS.test(whole)) return { ok: false, problem: 'invalid' }
  if (grouped && groups.length === 2 && decimalAt === -1) return { ok: false, problem: 'ambiguous' }
  if (fraction.length > maxFractionDigits) return { ok: false, problem: 'precision' }

  const digits = groups.join('')
  return { ok: true, value: `${sign}${digits}${fraction ? `.${fraction}` : ''}` }
}

/** Whether a parsed decimal string is greater than zero, read without a float. */
export function isPositiveDecimal(value: string): boolean {
  return !value.startsWith('-') && /[1-9]/.test(value)
}
